/**
 * WHAT A SHOP'S PAGES LEARN ABOUT ITS LANGUAGES, REMEMBERED.
 *
 * Mounted once in App.js beside `RtlSync`. Every shop page loads its business
 * through getSalonBySlug / getSalonByHost, which remember it (utils/currentShop);
 * when that shop is the one the address belongs to, its languages are written
 * down for the NEXT page -- which is how an owner's change to them reaches a
 * browser that already remembered the old list.
 *
 * It does not turn the language. Turning it here, after the page had mounted,
 * rebuilt the page (App.js keys its root on the language) and every event the
 * page reports on mount went out twice. The language is settled before a shop
 * page renders, by `ShopLanguageGate`.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { isDomainMode, getDomainHost } from '../../utils/domainMode';
import { useCurrentShop } from '../../utils/currentShop';
import {
  isShopLanguagePath,
  rememberShopLanguages,
  shopKeyOfPath,
  shopLanguagesOf,
} from '../../utils/shopLanguage';

export default function ShopLanguageSync() {
  const { pathname } = useLocation();
  const salon = useCurrentShop();

  useEffect(() => {
    if (!salon) return;
    const domainMode = isDomainMode();
    if (!isShopLanguagePath(pathname, domainMode)) return;
    const key = shopKeyOfPath(pathname, domainMode, getDomainHost());
    // On stylon.online the address names the shop; a shop remembered from a
    // moment ago never writes its languages down for another business's page.
    if (!domainMode && key !== (salon.slug ?? salon.username)) return;
    rememberShopLanguages(key, shopLanguagesOf(salon));
  }, [pathname, salon]);

  return null;
}
